import { useEffect, useState } from "react";
import { BellRing, Save, Send, Trash2 } from "lucide-react";
import { LoadingState, Textarea } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { ApiError } from "../../../../lib/api";
import { getPushSettings, savePushKey, sendTestPush, type PushSettings } from "../../../../lib/push";
import { Btn } from "../../../ui";
import { ConnStatus, IntegrationCard } from "../panel/IntegrationCard";
import { Field, Note, SaveRow } from "../panel/fields";

/**
 * Settings -> Phone -> Phone app: the Firebase key the app's push notices are sent
 * with. Today that is a new letter in the inbox; the calls ring through Twilio's own
 * push credential on the Connection tab, which stays as it is.
 *
 * The key is never shown again once saved: the card says whose it is, and the box
 * only ever takes a new one. The test sends a notice to the caller's own phones, so
 * a pasted key is proven on a real phone and not just stored.
 */
export function AppPushCard() {
  const t = useT();
  const [state, setState] = useState<PushSettings | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState<"save" | "remove" | "test" | null>(null);
  const [note, setNote] = useState<{ tone: "ok" | "bad" | "warn"; text: string } | null>(null);

  useEffect(() => {
    let live = true;
    getPushSettings()
      .then((res) => live && setState(res))
      .catch(() => live && setLoadError(true));
    return () => {
      live = false;
    };
  }, []);

  async function save(next: string | null) {
    setBusy(next === null ? "remove" : "save");
    setNote(null);
    try {
      const res = await savePushKey(next);
      setState(res);
      setDraft("");
      setNote({ tone: "ok", text: t(next === null ? "settings.appPush.removed" : "settings.appPush.saved") });
    } catch (err) {
      const invalid = err instanceof ApiError && err.status === 400;
      setNote({ tone: "bad", text: t(invalid ? "settings.appPush.invalidKey" : "settings.appPush.saveError") });
    } finally {
      setBusy(null);
    }
  }

  async function test() {
    setBusy("test");
    setNote(null);
    try {
      const res = await sendTestPush(t("settings.appPush.testTitle"), t("settings.appPush.testBody"));
      if (res.devices === 0) setNote({ tone: "warn", text: t("settings.appPush.noPhones") });
      else setNote({ tone: res.sent > 0 ? "ok" : "bad", text: t("settings.appPush.testResult", { sent: String(res.sent), devices: String(res.devices) }) });
    } catch {
      setNote({ tone: "bad", text: t("settings.appPush.testError") });
    } finally {
      setBusy(null);
    }
  }

  const status = state ? (
    <ConnStatus ok={state.keySet} okLabel={t("settings.appPush.on")} offLabel={t("settings.appPush.off")} />
  ) : null;

  return (
    <IntegrationCard icon={BellRing} title={t("settings.appPush.title")} desc={t("settings.appPush.desc")} status={status}>
      {loadError ? (
        <Note tone="bad">{t("settings.appPush.loadError")}</Note>
      ) : state === null ? (
        <LoadingState variant="inline" label={t("common.loading")} />
      ) : (
        <div className="flex flex-col gap-4">
          {state.keySet ? (
            <Note tone="ok">{t("settings.appPush.keyOf", { project: state.projectId, account: state.clientEmail })}</Note>
          ) : (
            <Note tone="info">{t("settings.appPush.howTo")}</Note>
          )}
          <Field label={t(state.keySet ? "settings.appPush.replace" : "settings.appPush.key")} hint={t("settings.appPush.keyHint")}>
            <Textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              rows={5}
              spellCheck={false}
              autoComplete="off"
              placeholder='{ "type": "service_account", ... }'
              className="font-mono text-xs"
            />
          </Field>
          {note ? (
            <Note tone={note.tone} live>
              {note.text}
            </Note>
          ) : null}
          <SaveRow>
            <Btn tone="dark" disabled={!draft.trim() || busy !== null} onClick={() => void save(draft.trim())}>
              <Save className="size-4" />
              {t("settings.appPush.save")}
            </Btn>
            <Btn disabled={!state.keySet || busy !== null} onClick={() => void test()}>
              <Send className="size-4" />
              {t("settings.appPush.test")}
            </Btn>
            {state.keySet ? (
              <Btn disabled={busy !== null} onClick={() => void save(null)}>
                <Trash2 className="size-4" />
                {t("settings.appPush.remove")}
              </Btn>
            ) : null}
          </SaveRow>
        </div>
      )}
    </IntegrationCard>
  );
}
